// hint-ladder — the student-side renderer for the three-tier hint ladder (tiered-hints, Trello
// 6WQKXSWl). Plain ESM, NO Lit, NO URL parsing: every video tier arrives already RESOLVED by the
// server (`lib/util/hint-ladder.js` `resolveHintVideo` → `{ href, title, startSeconds,
// endSeconds? }`). hint-video-end (Trello biGCccbA): a tier with an end arrives with a YouTube
// EMBED href that already carries `start`/`end`; this file only words the window.
//
// Tiers, in order, each present only when non-empty:
//   1. `hint`        — "Where to look"           (the existing single hint)
//   2. `hintVideo`   — "Watch how it's done"     (a link that opens at a timestamp, optionally
//                                                 stopping at an end time)
//   3. `hintPartial` — "Show part of the answer"
//
// Whether a surface uses the ladder at all is the SURFACE's decision: at the four existing hint
// sites an entry whose only tier is Hint 1 (`isLegacy`) keeps its existing, byte-identical code
// path. `renderLadder` itself renders whatever tiers it is given, a where-only entry included
// (the challenge-level ladder and completion's mixed mode rely on that).
//
// The ladder: a collapsed "Stuck? Get a hint" toggle (`aria-expanded`). Opening it reveals
// "Hint 1 of N: <label>" AT ONCE (the press is the request, so no second click); clicking a later
// tier button reveals that tier (inside an `aria-live="polite"` region) and shows the next tier's
// button. Reveals are ONE-WAY: a used
// tier button stays visible with `aria-expanded="true"` + `aria-disabled="true"` (never
// `disabled`, so it stays focusable) and further clicks do nothing. Closing the toggle hides the
// ladder but keeps what was revealed for this page load; nothing persists across loads.
//
// Styles: lib/views/includes/hint-ladder-styles.html (`hl-` classes, tokens only).

export var TIER_LABELS = {
  where   : 'Where to look',
  video   : "Watch how it's done",
  partial : 'Show part of the answer'
};

function trimmed(v) {
  return typeof v === 'string' ? v.trim() : '';
}

// -> ordered array of present tiers:
//    [{ kind:'where', text }, { kind:'video', href, title, startSeconds, endSeconds },
//     { kind:'partial', text }]
//    `startSeconds` / `endSeconds` are positive whole numbers, else null.
export function buildLadder(entry) {
  var tiers = [];
  if (!entry || typeof entry !== 'object') { return tiers; }
  var where = trimmed(entry.hint);
  if (where) { tiers.push({ kind : 'where', text : entry.hint }); }
  var v = entry.hintVideo;
  if (v && typeof v === 'object' && trimmed(v.href)) {
    tiers.push({
      kind         : 'video',
      href         : v.href,
      title        : trimmed(v.title),
      startSeconds : (typeof v.startSeconds === 'number' && isFinite(v.startSeconds) && v.startSeconds > 0)
        ? Math.floor(v.startSeconds) : null,
      endSeconds   : (typeof v.endSeconds === 'number' && isFinite(v.endSeconds) && v.endSeconds > 0 &&
        Math.floor(v.endSeconds) === v.endSeconds) ? v.endSeconds : null
    });
  }
  var partial = trimmed(entry.hintPartial);
  if (partial) { tiers.push({ kind : 'partial', text : entry.hintPartial }); }
  return tiers;
}

// True iff the ONLY present tier is "Where to look" — the surface then keeps its legacy path.
export function isLegacy(entry) {
  var tiers = buildLadder(entry);
  return tiers.length === 1 && tiers[0].kind === 'where';
}

// True iff the entry should render as a ladder at one of the four existing hint sites.
export function usesLadder(entry) {
  var tiers = buildLadder(entry);
  return tiers.length > 0 && !(tiers.length === 1 && tiers[0].kind === 'where');
}

function pad2(n) { return (n < 10 ? '0' : '') + n; }

// -> 'm:ss', or 'h:mm:ss' at 3600 seconds and above.
export function formatTime(seconds) {
  var s = Math.max(0, Math.floor(Number(seconds) || 0));
  var h = Math.floor(s / 3600);
  var m = Math.floor((s % 3600) / 60);
  var sec = s % 60;
  if (h > 0) { return h + ':' + pad2(m) + ':' + pad2(sec); }
  return m + ':' + pad2(sec);
}

// -> { sentence, linkText } for a video tier. With an end (hint-video-end) the sentence names
// the window ("from 0:30 to 0:45" / "up to 0:45"); the link text never changes, and a tier with
// no end words every string exactly as before.
export function videoCopy(tier) {
  var at = tier.startSeconds ? formatTime(tier.startSeconds) : '';
  var until = tier.endSeconds ? formatTime(tier.endSeconds) : '';
  var subject = tier.title ? 'Watch "' + tier.title + '"' : 'Watch the video';
  var sentence;
  if (until) {
    sentence = at ? subject + ' from ' + at + ' to ' + until + '.' : subject + ' up to ' + until + '.';
  } else if (tier.title) {
    sentence = at ? 'Watch "' + tier.title + '" from ' + at + '.' : 'Watch "' + tier.title + '".';
  } else {
    sentence = at ? 'Watch the video from ' + at + '.' : 'Watch the video.';
  }
  return { sentence : sentence, linkText : at ? 'Open the video at ' + at : 'Open the video' };
}

function defaultRenderText(el, text) {
  el.textContent = text;
}

function make(doc, tag, cls, text) {
  var n = doc.createElement(tag);
  if (cls) { n.className = cls; }
  if (text != null) { n.textContent = text; }
  return n;
}

function renderTierBody(doc, tier, renderText) {
  var body = make(doc, 'div', 'hl-tier-body hl-tier-' + tier.kind);
  if (tier.kind === 'video') {
    var copy = videoCopy(tier);
    var p = make(doc, 'p', 'hl-video');
    p.appendChild(doc.createTextNode(copy.sentence + ' '));
    var a = make(doc, 'a', 'hl-video-link', copy.linkText);
    a.setAttribute('href', tier.href);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener');
    p.appendChild(a);
    p.appendChild(doc.createTextNode(' '));
    p.appendChild(make(doc, 'span', 'hl-newtab', '(opens in a new tab)'));
    body.appendChild(p);
  } else {
    var t = make(doc, 'div', 'hl-text');
    renderText(t, tier.text);
    body.appendChild(t);
  }
  return body;
}

// -> a DOM node for the ladder. opts = { renderText(el, text), contextLabel }.
export function renderLadder(doc, entry, opts) {
  var o = opts || {};
  var renderText = typeof o.renderText === 'function' ? o.renderText : defaultRenderText;
  var tiers = buildLadder(entry);
  var n = tiers.length;

  var root = make(doc, 'div', 'hl-ladder');
  var toggle = make(doc, 'button', 'hl-toggle');
  toggle.type = 'button';
  toggle.setAttribute('aria-expanded', 'false');
  if (o.contextLabel) {
    toggle.setAttribute('aria-label', 'Stuck? Get a hint for ' + o.contextLabel);
  }
  toggle.appendChild(doc.createTextNode('Stuck? Get a hint'));
  root.appendChild(toggle);

  var body = make(doc, 'div', 'hl-body');
  body.hidden = true;
  root.appendChild(body);

  var items = [];
  var reveals = [];
  tiers.forEach(function(tier, i) {
    var item = make(doc, 'div', 'hl-tier');
    var btn = make(doc, 'button', 'hl-tier-btn', 'Hint ' + (i + 1) + ' of ' + n + ': ' + TIER_LABELS[tier.kind]);
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    var live = make(doc, 'div', 'hl-live');
    live.setAttribute('aria-live', 'polite');
    item.appendChild(btn);
    item.appendChild(live);
    // Only the first tier's button shows until it is used.
    item.hidden = i > 0;
    body.appendChild(item);
    items.push(item);

    function reveal() {
      if (btn.getAttribute('aria-expanded') === 'true') { return; }
      btn.setAttribute('aria-expanded', 'true');
      btn.setAttribute('aria-disabled', 'true');
      live.appendChild(renderTierBody(doc, tier, renderText));
      if (items[i + 1]) { items[i + 1].hidden = false; }
    }
    reveals.push(reveal);
    btn.addEventListener('click', reveal);
  });

  toggle.addEventListener('click', function() {
    var open = body.hidden;
    body.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    // Pressing "Stuck? Get a hint" IS the request for a hint, so the first one shows straight away
    // (a student should not have to ask twice). Later tiers still wait for their own button, because
    // each one gives more of the answer away. Idempotent: reopening never repeats it.
    if (open && reveals.length) { reveals[0](); }
  });

  return root;
}
